<template>
  <div class="app-container">
    <div v-if="isRouterReady" class="router-container">
      <ElConfigProvider :locale="locale">
        <AppProvider>
          <RouterView class="bg-layout" />
        </AppProvider>
      </ElConfigProvider>
    </div>
    <div v-else class="loading-container">
      <div class="loading-spinner"></div>
      <div class="loading-text">应用加载中...</div>
      <div v-if="loadingTimeout" class="loading-timeout">
        <p>加载时间过长，您可以：</p>
        <button @click="reloadApp">刷新页面</button>
        <button @click="forceShow">强制显示</button>
      </div>
    </div>

    <div v-if="hasError" class="error-container">
      <div class="error-message">
        <h2>应用加载出错</h2>
        <p>{{ errorMessage }}</p>
        <button @click="reloadApp">重新加载</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onErrorCaptured } from 'vue'
import { useRouter } from 'vue-router'
import { UILocales } from './locales/ui';
import { useAppStore } from './store/modules/app';

const appStore = useAppStore();

const locale = computed(() => {
  return UILocales[appStore.locale];
});

// 路由就绪状态
const isRouterReady = ref(false)
// 错误状态
const hasError = ref(false)
const errorMessage = ref('')
// 加载超时状态
const loadingTimeout = ref(false)

const router = useRouter()

// 设置全局错误处理
onErrorCaptured((err) => {
  console.error('App error captured:', err)
  hasError.value = true
  errorMessage.value = err instanceof Error ? err.message : String(err)
  return false // 阻止错误继续传播
})

// 监听路由就绪状态
onMounted(async () => {
  console.log('[App] Component mounted, waiting for router')

  // 设置8秒超时
  const timeout = setTimeout(() => {
    console.error('[App] Router ready timeout')
    loadingTimeout.value = true
  }, 8000)

  try {
    // 等待路由就绪
    await router.isReady()
    clearTimeout(timeout)

    console.log('[App] Router is ready')
    isRouterReady.value = true
  } catch (err) {
    console.error('[App] Router ready error:', err)
    clearTimeout(timeout)
    loadingTimeout.value = true
    hasError.value = true
    errorMessage.value = err instanceof Error ? err.message : '路由初始化失败'
  }
})

// 重新加载应用
function reloadApp() {
  window.location.reload()
}

// 强制显示内容
function forceShow() {
  console.log('[App] Forcing content display')
  isRouterReady.value = true
}
</script>

<style scoped>
.app-container {
  height: 100vh;
  width: 100vw;
  position: relative;
}

.router-container {
  height: 100%;
  width: 100%;
}

.loading-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f5f7fa;
  z-index: 9999;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #e4e7ed;
  border-radius: 50%;
  border-top-color: #409eff;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 18px;
  color: #606266;
  margin-bottom: 20px;
}

.loading-timeout {
  text-align: center;
  margin-top: 20px;
}

.loading-timeout p {
  font-size: 14px;
  color: #909399;
  margin-bottom: 10px;
}

.loading-timeout button {
  background-color: #409eff;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  margin: 0 5px;
}

.error-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 10000;
}

.error-message {
  background-color: white;
  padding: 30px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  text-align: center;
}

.error-message h2 {
  color: #f56c6c;
  margin-top: 0;
}

.error-message button {
  background-color: #409eff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>